import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  TableFooter,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grow,
  Tooltip,
  Avatar,
  Chip,
  CircularProgress,
  Divider,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import LockIcon from "@material-ui/icons/Lock";
import EventBusyOutlinedIcon from "@material-ui/icons/EventBusyOutlined";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import DateFnsUtils from "@date-io/date-fns";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import { adminSignup, updateUser, deleteUser } from "../../actions/auth";
import * as api from "../../api";
import UserRow from "../userRow";
import UserFormDialog from "./UserFormDialog";
import ConfirmDialog from "./ConfirmDialog";
import tableStyles from "./UsersTable.module.css";

const BLUR_BACKDROP = { style: { backdropFilter: "blur(3px)", backgroundColor: "rgba(15,23,42,0.35)" } };

// allowAdminActions/showExpiry/excludeUserId are only ever passed true/set
// by the SuperAdmin dashboard (SubscriptionComponent/SuperAdminUsersTab.js)
// - defaults here preserve this table's original store-admin behavior
// exactly (admin rows stay un-toggleable, no Expiry column). canDeleteAdmins/
// allowPasswordReveal are the same idea, superAdmin-only, never true for the
// store-admin usage.
const UsersTab = ({
  users,
  userTypes,
  onCreateUserType,
  openSnackbar,
  allowAdminActions = false,
  showExpiry = false,
  excludeUserId = null,
  canDeleteAdmins = false,
  allowPasswordReveal = false,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  // A 7-column table with 6 action icons per row has no honest way to fit a
  // phone - below this width the same data renders as cards instead, rather
  // than as a table the user has to scroll sideways through.
  // down("xs") is <600px. NOT down("sm"), which in MUI v4 means <960px and
  // would swap tablets to the card layout too, out of step with the 600px
  // breakpoint the sidebar/toolbar CSS uses.
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formOpen, setFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [expiryTarget, setExpiryTarget] = useState(null);
  const [expiryValue, setExpiryValue] = useState(null);
  const [expirySaving, setExpirySaving] = useState(false);
  const [viewTarget, setViewTarget] = useState(null);
  const [viewStoreInfo, setViewStoreInfo] = useState(null);
  const [viewStoreLoading, setViewStoreLoading] = useState(false);
  const [revealTarget, setRevealTarget] = useState(null);
  const [revealedPassword, setRevealedPassword] = useState("");
  const [revealLoading, setRevealLoading] = useState(false);
  const [revealError, setRevealError] = useState("");

  const filteredUsers = users.filter((u) => {
    // Hides the platform's own seed superAdmin account from the store-admin
    // usage only (allowAdminActions is only ever true for the SuperAdmin
    // dashboard) - the superAdmin's own table should still show every
    // superAdmin row, including one using this exact seed email, or their
    // own account could disappear from their own user list.
    if (!allowAdminActions && u.email === "superAdmin@gmail.com") return false;
    if (excludeUserId && u._id === excludeUserId) return false;
    return u.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const isUserAdmin = (u) => Array.isArray(u.userType) && u.userType.includes("admin");
  const isUserSuperAdmin = (u) => Array.isArray(u.userType) && u.userType.includes("superAdmin");

  const openCreate = () => {
    setEditingUser(null);
    setFormOpen(true);
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setFormOpen(true);
  };

  const handleCreate = (payload, snackbar, setSaving, onSuccess) => {
    dispatch(adminSignup(payload, snackbar, setSaving, onSuccess));
  };

  const handleUpdate = (id, payload, snackbar) => {
    // Returned (not fire-and-forget) so UserFormDialog.js can await it and
    // only close/reset its saving state once the request actually finishes.
    return dispatch(updateUser(id, payload, snackbar));
  };

  const closePasswordDialog = () => {
    setPasswordTarget(null);
    setNewPassword("");
    setConfirmPassword("");
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleChangePasswordSubmit = async () => {
    if (!passwordTarget?._id) return;
    if (!newPassword || newPassword.length < 6) {
      openSnackbar("Password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      openSnackbar("Passwords do not match");
      return;
    }
    setPasswordChanging(true);
    try {
      await api.adminChangePassword(passwordTarget._id, newPassword);
      openSnackbar("Password updated successfully");
      closePasswordDialog();
    } catch (err) {
      openSnackbar(err?.response?.data?.message || "Failed to update password");
    } finally {
      setPasswordChanging(false);
    }
  };

  const closeExpiryDialog = () => {
    setExpiryTarget(null);
    setExpiryValue(null);
  };

  const openExpiryDialog = (u) => {
    setExpiryTarget(u);
    setExpiryValue(u.accountExpiry ? new Date(u.accountExpiry) : null);
  };

  const handleSaveExpiry = async () => {
    if (!expiryTarget?._id) return;
    setExpirySaving(true);
    try {
      await dispatch(
        updateUser(
          expiryTarget._id,
          { accountExpiry: expiryValue ? expiryValue.toISOString() : null },
          openSnackbar
        )
      );
      closeExpiryDialog();
    } catch (error) {
      // Error already shown via openSnackbar inside the updateUser thunk -
      // leave the dialog open so the admin can retry.
    } finally {
      setExpirySaving(false);
    }
  };

  const closeRevealDialog = () => {
    setRevealTarget(null);
    setRevealedPassword("");
    setRevealError("");
  };

  const openRevealDialog = async (u) => {
    setRevealTarget(u);
    setRevealedPassword("");
    setRevealError("");
    // Skip the request entirely when we already know there's nothing to
    // reveal (getUsers's hasStoredPassword flag) - avoids a guaranteed 404
    // round-trip and shows the same explanation instantly.
    if (!u.hasStoredPassword) {
      setRevealError("No stored password yet - this account's password was set before reveal was enabled, or has never been changed. Use Reset Password to set a new one.");
      return;
    }
    setRevealLoading(true);
    try {
      const { data } = await api.revealUserPassword(u._id);
      setRevealedPassword(data.password);
    } catch (err) {
      setRevealError(err?.response?.data?.message || "Not available - reset required");
    } finally {
      setRevealLoading(false);
    }
  };

  const handleCopyRevealedPassword = async () => {
    try {
      await navigator.clipboard.writeText(revealedPassword);
      openSnackbar("Password copied to clipboard");
    } catch (err) {
      openSnackbar("Could not copy - please copy manually");
    }
  };

  // Fetches the store this user belongs to (business name/address/phone)
  // only while the View Profile dialog for them is open - not needed for
  // every row up front.
  useEffect(() => {
    if (!viewTarget?._id) {
      setViewStoreInfo(null);
      return;
    }
    let cancelled = false;
    setViewStoreLoading(true);
    setViewStoreInfo(null);
    api
      .fetchProfileByUserId(viewTarget._id)
      .then(({ data }) => {
        if (!cancelled) setViewStoreInfo(data);
      })
      .catch(() => {
        if (!cancelled) setViewStoreInfo(false);
      })
      .finally(() => {
        if (!cancelled) setViewStoreLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [viewTarget]);

  const expiryStatus = (u) => {
    if (!u.accountExpiry) return { label: "No expiry", color: "#94a3b8" };
    const daysLeft = Math.ceil((new Date(u.accountExpiry).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return { label: `Expired ${Math.abs(daysLeft)}d ago`, color: "#dc2626" };
    if (daysLeft <= 7) return { label: `${daysLeft}d left`, color: "#ea580c" };
    return { label: `${daysLeft}d left`, color: "#16a34a" };
  };

  const pagedUsers =
    rowsPerPage > 0
      ? filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : filteredUsers;

  const srNoFor = (index) => (rowsPerPage > 0 ? page * rowsPerPage + index + 1 : index + 1);

  const initialsFor = (name) =>
    (name || "?")
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

  // Shared by the desktop table row and the mobile card below - six buttons
  // duplicated across two layouts would drift the first time one of them
  // gained a permission rule.
  const renderActions = (u) => (
    <>
      <Tooltip title="View profile" arrow>
        <IconButton size="small" className={`${tableStyles.actionBtn} ${tableStyles.actionView}`} onClick={() => setViewTarget(u)}>
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Edit" arrow>
        <IconButton size="small" className={`${tableStyles.actionBtn} ${tableStyles.actionEdit}`} onClick={() => openEdit(u)}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Reset password" arrow>
        <IconButton size="small" className={`${tableStyles.actionBtn} ${tableStyles.actionReset}`} onClick={() => setPasswordTarget(u)}>
          <LockIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {allowPasswordReveal && (
        <Tooltip title={u.hasStoredPassword ? "Reveal password" : "No stored password yet - click for details"} arrow>
          {/* Not disabled even when hasStoredPassword is false - a disabled
              IconButton never fires onClick, so the user just sees a dead
              icon with no explanation. Always clickable; the dialog itself
              shows why nothing is available (see revealError below) plus a
              shortcut to fix it via Reset Password. */}
          <IconButton
            size="small"
            className={`${tableStyles.actionBtn} ${tableStyles.actionReveal}`}
            style={u.hasStoredPassword ? undefined : { opacity: 0.45 }}
            onClick={() => openRevealDialog(u)}
          >
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {showExpiry && !isUserSuperAdmin(u) && (
        <Tooltip title="Set expiry" arrow>
          <IconButton size="small" className={`${tableStyles.actionBtn} ${tableStyles.actionExpiry}`} onClick={() => openExpiryDialog(u)}>
            <EventBusyOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {(!isUserAdmin(u) || canDeleteAdmins) && !isUserSuperAdmin(u) && (
        <Tooltip title="Delete" arrow>
          <IconButton size="small" className={`${tableStyles.actionBtn} ${tableStyles.actionDelete}`} onClick={() => setDeleteTarget(u)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  const paginationProps = {
    rowsPerPageOptions: [10, 25, 50, { label: "All", value: -1 }],
    count: filteredUsers.length,
    rowsPerPage,
    page,
    onPageChange: (_e, newPage) => setPage(newPage),
    onRowsPerPageChange: (e) => {
      setRowsPerPage(parseInt(e.target.value, 10));
      setPage(0);
    },
  };

  return (
    <div>
      <div className={tableStyles.toolbar}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={tableStyles.searchField}
          fullWidth={isMobile}
        />
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={openCreate}
          style={{ color: "#ef7f1a", borderColor: "#ef7f1a" }}
          fullWidth={isMobile}
        >
          Add User
        </Button>
      </div>

      {isMobile ? (
        <div className={tableStyles.mobileList}>
          {pagedUsers.map((u, index) => (
            <div key={u._id} className={tableStyles.userCard}>
              <div className={tableStyles.userCardHead}>
                <Avatar className={tableStyles.userCardAvatar}>{initialsFor(u.name)}</Avatar>
                <div className={tableStyles.userCardIdentity}>
                  <div className={tableStyles.userCardName}>{u.name}</div>
                  <div className={tableStyles.userCardEmail}>{u.email}</div>
                </div>
                <span className={tableStyles.userCardSr}>#{srNoFor(index)}</span>
              </div>

              <div className={tableStyles.userCardMeta}>
                <Chip
                  size="small"
                  className={tableStyles.userCardChip}
                  label={Array.isArray(u.userType) ? u.userType.join(", ") : u.userType || "user"}
                />
                {showExpiry && (
                  <span
                    className={tableStyles.userCardExpiry}
                    style={{ color: isUserSuperAdmin(u) ? "#94a3b8" : expiryStatus(u).color }}
                  >
                    {isUserSuperAdmin(u) ? "No expiry (SuperAdmin)" : expiryStatus(u).label}
                  </span>
                )}
                <div className={tableStyles.userCardToggle}>
                  <UserRow user={u} disabled={!allowAdminActions && isUserAdmin(u)} openSnackbar={openSnackbar} />
                </div>
              </div>

              <div className={tableStyles.userCardActions}>{renderActions(u)}</div>
            </div>
          ))}

          {pagedUsers.length === 0 && (
            <div className={tableStyles.emptyState}>No users match this search.</div>
          )}

          <TablePagination component="div" {...paginationProps} />
        </div>
      ) : (
      <div className={tableStyles.tableCard}>
      <TableContainer style={{ overflowX: "auto" }}>
        <Table>
          <TableHead>
            <TableRow className={tableStyles.headRow}>
              <TableCell>Sr No</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Type</TableCell>
              {showExpiry && <TableCell>Expiry</TableCell>}
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedUsers.map((u, index) => (
              <TableRow key={u._id} className={tableStyles.bodyRow}>
                <TableCell>{srNoFor(index)}</TableCell>
                <TableCell>
                  <div className={tableStyles.cellIdentity}>
                    <Avatar className={tableStyles.rowAvatar}>{initialsFor(u.name)}</Avatar>
                    <span className={tableStyles.cellName}>{u.name}</span>
                  </div>
                </TableCell>
                <TableCell className={tableStyles.cellEmail}>{u.email}</TableCell>
                <TableCell>
                  <UserRow user={u} disabled={!allowAdminActions && isUserAdmin(u)} openSnackbar={openSnackbar} />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    className={tableStyles.userCardChip}
                    label={Array.isArray(u.userType) ? u.userType.join(", ") : u.userType || "user"}
                  />
                </TableCell>
                {showExpiry && (
                  <TableCell>
                    {isUserSuperAdmin(u) ? (
                      <span style={{ color: "#94a3b8", fontSize: "0.8rem" }}>N/A</span>
                    ) : (
                      <span style={{ color: expiryStatus(u).color, fontSize: "0.8rem", fontWeight: 600 }}>
                        {expiryStatus(u).label}
                      </span>
                    )}
                  </TableCell>
                )}
                <TableCell align="right" className={tableStyles.actionsCell}>
                  {renderActions(u)}
                </TableCell>
              </TableRow>
            ))}
            {pagedUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={showExpiry ? 7 : 6} className={tableStyles.emptyState}>
                  No users match this search.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination {...paginationProps} colSpan={showExpiry ? 7 : 6} />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
      </div>
      )}

      <UserFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        user={editingUser}
        userTypes={userTypes}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onCreateUserType={onCreateUserType}
        openSnackbar={openSnackbar}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete User"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? This can't be undone.`}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          dispatch(deleteUser(deleteTarget._id, openSnackbar));
          setDeleteTarget(null);
        }}
      />

      <Dialog
        open={!!passwordTarget}
        onClose={closePasswordDialog}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Grow}
        BackdropProps={BLUR_BACKDROP}
      >
        <DialogTitle style={{ backgroundColor: "#ef7f1a", color: "#fff" }}>
          Change Password
          {passwordTarget && (
            <span style={{ fontSize: 14, opacity: 0.9, display: "block", marginTop: 4 }}>
              {passwordTarget.name} ({passwordTarget.email})
            </span>
          )}
        </DialogTitle>
        <DialogContent style={{ padding: 24, paddingTop: 20 }}>
          <TextField
            autoFocus
            margin="dense"
            label="New Password"
            type={showNewPassword ? "text" : "password"}
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            inputProps={{ minLength: 6 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end" size="small">
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="dense"
            label="Confirm Password"
            type={showConfirmPassword ? "text" : "password"}
            fullWidth
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" size="small">
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closePasswordDialog}>Cancel</Button>
          <Button
            onClick={handleChangePasswordSubmit}
            variant="contained"
            style={{ backgroundColor: "#ef7f1a", color: "#fff" }}
            disabled={passwordChanging}
          >
            {passwordChanging ? "Updating..." : "Update Password"}
          </Button>
        </DialogActions>
      </Dialog>

      {showExpiry && (
        <Dialog
          open={!!expiryTarget}
          onClose={closeExpiryDialog}
          maxWidth="xs"
          fullWidth
          TransitionComponent={Grow}
          BackdropProps={BLUR_BACKDROP}
        >
          <DialogTitle style={{ backgroundColor: "#ef7f1a", color: "#fff" }}>
            Set Account Expiry
            {expiryTarget && (
              <span style={{ fontSize: 14, opacity: 0.9, display: "block", marginTop: 4 }}>
                {expiryTarget.name} ({expiryTarget.email})
              </span>
            )}
          </DialogTitle>
          <DialogContent style={{ padding: 24, paddingTop: 20 }}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                autoFocus
                fullWidth
                disableToolbar
                variant="inline"
                inputVariant="outlined"
                format="MMM dd, yyyy"
                margin="dense"
                label="Expiry date"
                value={expiryValue}
                onChange={(date) => setExpiryValue(date)}
                helperText="Leave blank to clear - the account will never expire on its own."
                KeyboardButtonProps={{ "aria-label": "change expiry date" }}
              />
            </MuiPickersUtilsProvider>
          </DialogContent>
          <DialogActions>
            {expiryTarget?.accountExpiry && (
              <Button onClick={() => setExpiryValue(null)} disabled={expirySaving}>
                Clear
              </Button>
            )}
            <Button onClick={closeExpiryDialog} disabled={expirySaving}>Cancel</Button>
            <Button
              onClick={handleSaveExpiry}
              variant="contained"
              style={{ backgroundColor: "#ef7f1a", color: "#fff" }}
              disabled={expirySaving}
            >
              {expirySaving ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog
        open={!!viewTarget}
        onClose={() => setViewTarget(null)}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Grow}
        BackdropProps={BLUR_BACKDROP}
      >
        <DialogTitle style={{ backgroundColor: "#ef7f1a", color: "#fff" }}>
          User Profile
        </DialogTitle>
        {viewTarget && (
          <DialogContent style={{ padding: 24, paddingTop: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
              <Avatar style={{ backgroundColor: "#ef7f1a", width: 52, height: 52, fontWeight: 700 }}>
                {(viewTarget.name || "?").split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)}
              </Avatar>
              <div>
                <div style={{ fontWeight: 700, fontSize: "1.05rem" }}>{viewTarget.name}</div>
                <div style={{ color: "#64748b", fontSize: "0.85rem" }}>{viewTarget.email}</div>
              </div>
            </div>
            <Divider style={{ marginBottom: 14 }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ color: "#64748b", fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase" }}>Status</span>
                <Chip
                  size="small"
                  label={viewTarget.isActive === false ? "Inactive" : "Active"}
                  style={{
                    backgroundColor: viewTarget.isActive === false ? "#fef2f2" : "#f0fdf4",
                    color: viewTarget.isActive === false ? "#dc2626" : "#16a34a",
                    fontWeight: 700,
                  }}
                />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ color: "#64748b", fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase" }}>Role</span>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  {(Array.isArray(viewTarget.userType) ? viewTarget.userType : [viewTarget.userType || "user"]).map((t) => (
                    <Chip key={t} size="small" label={t} />
                  ))}
                </div>
              </div>
              {showExpiry && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ color: "#64748b", fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase" }}>Expiry</span>
                  <span style={{ fontWeight: 600, color: isUserSuperAdmin(viewTarget) ? "#94a3b8" : expiryStatus(viewTarget).color }}>
                    {isUserSuperAdmin(viewTarget) ? "N/A" : expiryStatus(viewTarget).label}
                  </span>
                </div>
              )}
              {viewTarget.createdAt && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ color: "#64748b", fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase" }}>Created</span>
                  <span style={{ fontWeight: 600 }}>{new Date(viewTarget.createdAt).toLocaleDateString()}</span>
                </div>
              )}
            </div>

            <Divider style={{ margin: "16px 0" }} />
            <div style={{ color: "#64748b", fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>
              Store
            </div>
            {viewStoreLoading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 12 }}>
                <CircularProgress size={22} style={{ color: "#ef7f1a" }} />
              </div>
            ) : viewStoreInfo && viewStoreInfo.businessName ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>{viewStoreInfo.businessName}</div>
                {viewStoreInfo.contactAddress && (
                  <div style={{ color: "#475569", fontSize: "0.85rem" }}>{viewStoreInfo.contactAddress}</div>
                )}
                {viewStoreInfo.phoneNumber && (
                  <div style={{ color: "#475569", fontSize: "0.85rem" }}>{viewStoreInfo.phoneNumber}</div>
                )}
                {viewStoreInfo.website && (
                  <div style={{ color: "#475569", fontSize: "0.85rem" }}>{viewStoreInfo.website}</div>
                )}
              </div>
            ) : (
              <div style={{ color: "#94a3b8", fontSize: "0.85rem" }}>No store details available</div>
            )}
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setViewTarget(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {allowPasswordReveal && (
        <Dialog
          open={!!revealTarget}
          onClose={closeRevealDialog}
          maxWidth="xs"
          fullWidth
          TransitionComponent={Grow}
          BackdropProps={BLUR_BACKDROP}
        >
          <DialogTitle style={{ backgroundColor: "#ef7f1a", color: "#fff" }}>
            Reveal Password
            {revealTarget && (
              <span style={{ fontSize: 14, opacity: 0.9, display: "block", marginTop: 4 }}>
                {revealTarget.name} ({revealTarget.email})
              </span>
            )}
          </DialogTitle>
          <DialogContent style={{ padding: 24, paddingTop: 20, minHeight: 80 }}>
            {revealLoading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 20 }}>
                <CircularProgress size={28} style={{ color: "#ef7f1a" }} />
              </div>
            ) : revealError ? (
              <div>
                <div style={{ color: "#dc2626", fontSize: "0.9rem" }}>{revealError}</div>
                <Button
                  size="small"
                  startIcon={<LockIcon fontSize="small" />}
                  style={{ marginTop: 14, color: "#ef7f1a" }}
                  onClick={() => {
                    const target = revealTarget;
                    closeRevealDialog();
                    setPasswordTarget(target);
                  }}
                >
                  Reset password instead
                </Button>
              </div>
            ) : (
              <TextField
                fullWidth
                label="Current password"
                value={revealedPassword}
                variant="outlined"
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Copy to clipboard" arrow>
                        <IconButton onClick={handleCopyRevealedPassword} edge="end" size="small">
                          <FileCopyOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeRevealDialog}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

export default UsersTab;
